import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import Button from "components/CustomButtons/Button.js";
import { useCookies } from "react-cookie";
import { API_URI } from "../../helpers";

const useStyles = makeStyles({
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  tabs: {
    display: "flex",
    gap: 8,
    marginBottom: 16,
  },
  tab: {
    padding: "6px 16px",
    border: "1px solid #ccc",
    borderRadius: 20,
    cursor: "pointer",
    background: "#fff",
    fontSize: 13,
    "&:hover": { background: "#f5f5f5" },
  },
  tabActive: {
    padding: "6px 16px",
    border: "1px solid #4caf50",
    borderRadius: 20,
    cursor: "pointer",
    background: "#4caf50",
    color: "#fff",
    fontSize: 13,
  },
  searchInput: {
    padding: "8px 14px",
    border: "1px solid #ddd",
    borderRadius: 20,
    fontSize: 14,
    width: 280,
    outline: "none",
    "&:focus": { borderColor: "#4caf50" },
  },
  convList: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },
  convItem: {
    display: "flex",
    alignItems: "center",
    padding: "12px 16px",
    borderBottom: "1px solid #f0f0f0",
    cursor: "pointer",
    transition: "background 0.15s",
    "&:hover": { background: "#f9f9f9" },
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: "50%",
    background: "#4caf50",
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    fontSize: 18,
    marginRight: 14,
    flexShrink: 0,
  },
  convInfo: {
    flex: 1,
    overflow: "hidden",
  },
  convName: {
    fontWeight: 600,
    fontSize: 14,
    marginBottom: 2,
  },
  convPhone: {
    fontSize: 12,
    color: "#888",
  },
  convPreview: {
    fontSize: 13,
    color: "#666",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: 400,
  },
  convTime: {
    fontSize: 11,
    color: "#999",
    marginLeft: 12,
    whiteSpace: "nowrap",
  },
  statusBadge: {
    fontSize: 10,
    padding: "2px 8px",
    borderRadius: 10,
    marginLeft: 8,
    fontWeight: 600,
  },
  statusOpen: {
    background: "#e8f5e9",
    color: "#2e7d32",
  },
  statusClosed: {
    background: "#fafafa",
    color: "#999",
  },
  emptyState: {
    textAlign: "center",
    padding: 60,
    color: "#999",
    fontSize: 15,
  },
  pagination: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 12,
    marginTop: 16,
  },
  workerBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    padding: "4px 12px",
    borderRadius: 16,
    fontWeight: 500,
    cursor: "default",
  },
  workerDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    display: "inline-block",
  },
});

function formatTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  if (diff < 86400000) {
    return d.toLocaleTimeString("es-AR", { hour: "2-digit", minute: "2-digit" });
  }
  if (diff < 86400000 * 7) {
    return d.toLocaleDateString("es-AR", { weekday: "short" });
  }
  return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit" });
}

export default function WhatsAppInbox(props) {
  const classes = useStyles();
  const [cookies] = useCookies();
  const [conversations, setConversations] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [status, setStatus] = useState("open");
  const [search, setSearch] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [loading, setLoading] = useState(false);
  const [workerStatus, setWorkerStatus] = useState(null);

  // Polling del estado del worker
  useEffect(function () {
    function checkWorker() {
      fetch(API_URI + "/admin/whatsapp/queue-status")
        .then(function (r) { return r.json(); })
        .then(function (res) {
          if (res.result) setWorkerStatus(res);
        })
        .catch(function () {
          setWorkerStatus({ status: "down", pending: 0, failed: 0 });
        });
    }
    checkWorker();
    var interval = setInterval(checkWorker, 30000);
    return function () { clearInterval(interval); };
  }, []);

  const fetchConversations = useCallback(
    (page = 1) => {
      setLoading(true);
      const params = new FormData();
      params.append("page", page);
      params.append("status", status);
      if (search) params.append("search", search);

      fetch(API_URI + "/admin/whatsapp/conversations", {
        method: "POST",
        body: params,
      })
        .then((r) => r.json())
        .then((res) => {
          if (res.result) {
            setConversations(res.data);
            setPagination(res.pagination);
          }
          setLoading(false);
        })
        .catch(() => setLoading(false));
    },
    [status, search]
  );

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Polling cada 10 segundos
  useEffect(() => {
    const interval = setInterval(() => fetchConversations(), 10000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearch(val);
  };

  const openConversation = (conv) => {
    props.history.push("/admin/whatsapp-chat/" + conv.id);
  };

  const getInitials = (name, phone) => {
    if (name) {
      return name
        .split(" ")
        .map((w) => w[0])
        .join("")
        .substring(0, 2)
        .toUpperCase();
    }
    return phone ? phone.slice(-2) : "?";
  };

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardBody>
            <div className={classes.header}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <h3 style={{ margin: 0, fontWeight: 600 }}>
                  WhatsApp — Atención al público
                </h3>
                {workerStatus && (
                  <span
                    className={classes.workerBadge}
                    style={{
                      background:
                        workerStatus.status === "ok"
                          ? "#e8f5e9"
                          : workerStatus.status === "warning"
                          ? "#fff3e0"
                          : "#ffebee",
                      color:
                        workerStatus.status === "ok"
                          ? "#2e7d32"
                          : workerStatus.status === "warning"
                          ? "#e65100"
                          : "#c62828",
                    }}
                    title={
                      "Pendientes: " +
                      workerStatus.pending +
                      " | Fallidos: " +
                      workerStatus.failed
                    }
                  >
                    <span
                      className={classes.workerDot}
                      style={{
                        background:
                          workerStatus.status === "ok"
                            ? "#4caf50"
                            : workerStatus.status === "warning"
                            ? "#ff9800"
                            : "#f44336",
                      }}
                    />
                    {workerStatus.status === "ok"
                      ? "Worker activo"
                      : workerStatus.status === "warning"
                      ? workerStatus.failed + " jobs fallidos"
                      : "Worker caído"}
                  </span>
                )}
              </div>
              <input
                className={classes.searchInput}
                placeholder="Buscar por nombre o teléfono..."
                value={search}
                onChange={handleSearch}
              />
            </div>

            <div className={classes.tabs}>
              {[
                { key: "open", label: "Abiertas" },
                { key: "closed", label: "Cerradas" },
                { key: "archived", label: "Archivadas" },
                { key: "all", label: "Todas" },
              ].map((t) => (
                <div
                  key={t.key}
                  className={
                    status === t.key ? classes.tabActive : classes.tab
                  }
                  onClick={() => setStatus(t.key)}
                >
                  {t.label}
                </div>
              ))}
            </div>

            {loading && conversations.length === 0 ? (
              <div className={classes.emptyState}>Cargando...</div>
            ) : conversations.length === 0 ? (
              <div className={classes.emptyState}>
                No hay conversaciones
              </div>
            ) : (
              <ul className={classes.convList}>
                {conversations.map((conv) => (
                  <li
                    key={conv.id}
                    className={classes.convItem}
                    onClick={() => openConversation(conv)}
                  >
                    <div className={classes.avatar}>
                      {getInitials(conv.name, conv.phone)}
                    </div>
                    <div className={classes.convInfo}>
                      <div className={classes.convName}>
                        {conv.name || conv.phone}
                        <span
                          className={`${classes.statusBadge} ${
                            conv.status === "open"
                              ? classes.statusOpen
                              : classes.statusClosed
                          }`}
                        >
                          {conv.status}
                        </span>
                      </div>
                      {conv.name && (
                        <div className={classes.convPhone}>{conv.phone}</div>
                      )}
                      {conv.last_message && (
                        <div className={classes.convPreview}>
                          {conv.last_message.direction === "outgoing"
                            ? "Tú: "
                            : ""}
                          {conv.last_message.body || `[${conv.last_message.type}]`}
                        </div>
                      )}
                    </div>
                    <div className={classes.convTime}>
                      {formatTime(conv.last_message_at)}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {pagination && pagination.last_page > 1 && (
              <div className={classes.pagination}>
                <Button
                  size="sm"
                  disabled={pagination.current_page <= 1}
                  onClick={() =>
                    fetchConversations(pagination.current_page - 1)
                  }
                >
                  ← Anterior
                </Button>
                <span>
                  {pagination.current_page} / {pagination.last_page}
                </span>
                <Button
                  size="sm"
                  disabled={
                    pagination.current_page >= pagination.last_page
                  }
                  onClick={() =>
                    fetchConversations(pagination.current_page + 1)
                  }
                >
                  Siguiente →
                </Button>
              </div>
            )}
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
